import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DemoChecklist, isChecklistHidden, showChecklist } from "@/components/DemoChecklist";
import { clearCtx, fetchContext, readCtx, resetMyDemo, switchTo, waitForSaves } from "@/lib/roleContext";

const SIGNED_IN_AREAS = ["/app", "/portfolio", "/sales", "/diagnostic"];

// Persistent context bar: role + organization on every screen; DEMO — Fictional Data in demo workspaces.
const ContextBar = ({ ctx, checklistHidden, onShowChecklist }) => {
  useEffect(() => {
    document.body.classList.toggle("msx-ctx", !!ctx);
    return () => document.body.classList.remove("msx-ctx");
  }, [ctx]);
  if (!ctx) return null;
  const demo = ctx.environment === "demo";
  return (
    <div role="status" data-testid="context-banner"
      className={`fixed inset-x-0 bottom-0 z-[65] flex h-[34px] items-center justify-center gap-x-2 overflow-hidden whitespace-nowrap px-3 text-[12px] sm:text-[12.5px] font-semibold ${demo ? "bg-amber-400 text-[#1f1300]" : "bg-[#0f172a] text-white"}`}>
      {demo && <span data-testid="demo-indicator" className="inline-flex items-center gap-1"><FlaskConical className="h-4 w-4" />DEMO — Fictional Data</span>}
      {demo && <span aria-hidden>·</span>}
      <span data-testid="context-banner-role" className="min-w-0 truncate">{ctx.role_label} · {ctx.org_name}</span>
      {!demo && <span className="rounded bg-white/15 px-1.5 text-[10px] tracking-wider">PRODUCTION</span>}
      {demo && checklistHidden && (
        <button onClick={onShowChecklist} data-testid="demo-checklist-show"
          className="ml-1 shrink-0 rounded border border-[#1f1300]/30 px-1.5 text-[11px] hover:bg-[#1f1300]/10">Steps</button>
      )}
    </div>
  );
};

const Confirm = ({ open, title, body, cancel = "Cancel", ok, onOk, onCancel, testid, busy }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent data-testid={testid} className="max-w-md">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription asChild><div className="text-sm text-slate-600">{body}</div></AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`} onClick={onCancel}>{cancel}</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-ok`} disabled={busy} onClick={(e) => { e.preventDefault(); onOk(); }}>
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{ok}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const RoleContextController = () => {
  const [ctx, setCtx] = useState(readCtx);
  const [prodTarget, setProdTarget] = useState(null);
  const [unsavedTarget, setUnsavedTarget] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checklistHidden, setChecklistHidden] = useState(isChecklistHidden);

  const doSwitch = async (a) => {
    setBusy(true);
    try { await switchTo(a.id); } catch (e) { toast.error(e.message); setBusy(false); }
  };
  const afterSaves = async (a) => {
    toast.message("Saving your work…");
    if (await waitForSaves()) return doSwitch(a);
    setUnsavedTarget(a);
  };

  useEffect(() => {
    const onSwitch = (e) => {
      const { assignment: a, current } = e.detail;
      if (a.environment === "production" && current?.environment === "demo") setProdTarget(a);
      else afterSaves(a);
    };
    const onReset = () => setResetOpen(true);
    const onErr = (e) => {
      const c = e.detail?.code;
      if (c === "context_mismatch") { toast.error(e.detail.detail, { id: "ctx-mismatch" }); setTimeout(() => window.location.reload(), 1500); }
      if (c === "assignment_revoked") { toast.error(e.detail.detail, { id: "ctx-revoked" }); clearCtx(); setTimeout(() => window.location.assign("/"), 1800); }
      if (c === "demo_resetting") toast.message(e.detail.detail, { id: "ctx-resetting" });
    };
    window.addEventListener("msx-switch-request", onSwitch);
    window.addEventListener("msx-reset-request", onReset);
    window.addEventListener("msx-context-error", onErr);
    // Default starting assignment: applied once per tab (re-checked after SPA navigation, e.g. right after login).
    let tries = 0;
    const retry = () => { if (++tries < 3) sessionStorage.removeItem("ctx_default_checked"); };
    const checkDefault = () => {
      const p = window.location.pathname;
      if (readCtx() || sessionStorage.getItem("ctx_default_checked") || !SIGNED_IN_AREAS.some((x) => p.startsWith(x)) || p.includes("login")) return;
      sessionStorage.setItem("ctx_default_checked", "1");
      fetchContext().then((d) => {
        const def = d?.assignments?.find((a) => a.is_default);
        if (def && d.current?.id === "home") switchTo(def.id).catch(() => {});
        else if (!d) retry();
      }).catch(retry);
    };
    checkDefault();
    const t = setInterval(() => { setCtx(readCtx()); checkDefault(); }, 1500);
    return () => {
      window.removeEventListener("msx-switch-request", onSwitch);
      window.removeEventListener("msx-reset-request", onReset);
      window.removeEventListener("msx-context-error", onErr);
      clearInterval(t);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runReset = async () => {
    setBusy(true);
    try {
      const d = await resetMyDemo();
      toast.success(`Demo restored to the starting dataset (version ${d.seed_version}).`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(e.message); setBusy(false); setResetOpen(false); }
  };

  return (
    <>
      <ContextBar ctx={ctx} checklistHidden={checklistHidden} onShowChecklist={() => { showChecklist(); setChecklistHidden(false); }} />
      {ctx?.environment === "demo" && <DemoChecklist hidden={checklistHidden} setHidden={setChecklistHidden} />}
      <Confirm open={!!prodTarget} testid="prod-switch-confirm" title="Leave the demo and enter Production?"
        body={<>You're about to work with <b>real data</b> as <b>{prodTarget?.role_label}</b> at <b>{prodTarget?.org_name}</b>. Changes there affect real records.</>}
        ok={`Enter ${prodTarget?.org_name || "Production"}`} busy={busy}
        onCancel={() => setProdTarget(null)} onOk={() => { const a = prodTarget; setProdTarget(null); afterSaves(a); }} />
      <Confirm open={!!unsavedTarget} testid="unsaved-switch-confirm" title="Some changes haven't saved"
        body="We couldn't confirm that your latest changes were saved. Stay to finish them, or discard them and switch."
        cancel="Stay" ok="Discard and Switch" busy={busy}
        onCancel={() => setUnsavedTarget(null)} onOk={() => doSwitch(unsavedTarget)} />
      <Confirm open={resetOpen} testid="reset-demo-confirm" title="Reset My Demo?"
        body={<>This restores <b>your</b> demo workspace to its starting dataset: the fictional network, six schools, and their school and portfolio assessments. All demo edits you've made are erased. Your login, role assignments and security history are kept. Other people's demos aren't affected.</>}
        ok="Reset My Demo" busy={busy} onCancel={() => setResetOpen(false)} onOk={runReset} />
    </>
  );
};
